import type {
  AuditPatchAssigneesRequest,
  AuditPatchStartDateRequest,
  AuditCloneQueryParams,
  AuditSendRequest,
  AuditStatus } from '@arhs-fleetback/types/entities/audits';

import type { NavigateOptions } from 'react-router';

import { AUDITS_API_URL } from 'constants/ApiConstants';
import type { MetaAction } from 'modules/global/generic.types';

import * as actionTypes from '../actionTypes';
import { DETAILS_SHARE_MODAL, DETAILS_SOURCE } from '../constants';
import type * as types from '../types/details';
import type { DetailedAudit } from '../types/projections/detailed-audit';
import type { ExplodedViewMarker } from '../types/resources/questions/responses/exploded-view-marker';

export const fetchAudit = (auditId: string, meta: MetaAction = {}): types.FetchAuditAction => ({
  type: actionTypes.FETCH_AUDIT_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${AUDITS_API_URL}/${auditId}`,
      params: {
        auditId,
      },
    },
  },
  meta,
});

export const fetchAuditSuccess = (
  audit: DetailedAudit,
  meta: MetaAction = {},
): types.FetchAuditSuccessAction => ({
  type: actionTypes.FETCH_AUDIT_SUCCESS,
  payload: audit,
  meta,
});

export const fetchAuditFailure = (message: string, meta: MetaAction = {}): types.FetchAuditFailureAction => ({
  type: actionTypes.FETCH_AUDIT_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const postAudit = (
  payload: types.PostAuditRequestPayload,
  navigateState: NavigateOptions['state'],
  meta: MetaAction = {},
): types.PostAuditRequestAction => ({
  type: actionTypes.POST_REQUEST,
  payload: {
    navigateState,
    request: {
      method: 'POST',
      url: AUDITS_API_URL,
      data: payload,
    },
  },
  meta,
});

export const postSuccess = (audit: DetailedAudit, meta: MetaAction = {}): types.PostAuditSuccessAction => ({
  type: actionTypes.POST_SUCCESS,
  payload: audit,
  meta,
});

export const postFailure = (message: string, meta: MetaAction = {}): types.PostAuditFailureAction => ({
  type: actionTypes.POST_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const postClone = (
  auditId: string,
  params: AuditCloneQueryParams,
  navigateState: NavigateOptions['state'],
  meta: MetaAction = {},
): types.PostCloneAction => ({
  type: actionTypes.POST_CLONE_REQUEST,
  payload: {
    navigateState,
    request: {
      method: 'POST',
      url: `${AUDITS_API_URL}/${auditId}/clone`,
      params,
    },
  },
  meta,
});

export const postCloneSuccess = (
  audit: DetailedAudit,
  meta: MetaAction = {},
): types.PostCloneSuccessAction => ({
  type: actionTypes.POST_CLONE_SUCCESS,
  payload: audit,
  meta,
});

export const postCloneFailure = (
  message: string,
  meta: MetaAction = {},
): types.PostCloneFailureAction => ({
  type: actionTypes.POST_CLONE_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const putRequest = (
  audit: DetailedAudit,
  params: Record<string, unknown> | undefined = undefined,
  meta: MetaAction = {},
): types.PutAuditRequestAction => ({
  type: actionTypes.PUT_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${AUDITS_API_URL}/${audit.id}`,
      data: audit,
      params,
    },
  },
  meta,
});

export const putSuccess = (audit: DetailedAudit, meta: MetaAction = {}): types.PutAuditSuccessAction => ({
  type: actionTypes.PUT_SUCCESS,
  payload: audit,
  meta: {
    source: DETAILS_SOURCE,
    ...meta,
  },
});

export const putFailure = (message: string, meta: MetaAction = {}): types.PutAuditFailureAction => ({
  type: actionTypes.PUT_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const deleteRequest = (
  auditId: string,
  meta: MetaAction = {},
): types.DeleteAuditRequestAction => ({
  type: actionTypes.DELETE_REQUEST,
  payload: {
    auditId,
    request: {
      method: 'DELETE',
      url: `${AUDITS_API_URL}/${auditId}`,
      params: {
        auditId,
      },
    },
  },
  meta,
});

export const deleteSuccess = (
  auditId: string,
  meta: MetaAction = {},
): types.DeleteAuditSuccessAction => ({
  type: actionTypes.DELETE_SUCCESS,
  payload: auditId,
  meta,
});

export const deleteFailure = (message: string, meta: MetaAction = {}): types.DeleteAuditFailureAction => ({
  type: actionTypes.DELETE_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const patchStatusRequest = (
  { id: auditId, status }: { id: string; status: AuditStatus },
  meta: MetaAction = {},
): types.PatchAuditRequestAction => ({
  type: actionTypes.PATCH_STATUS_REQUEST,
  payload: {
    auditId,
    request: {
      method: 'PATCH',
      url: `${AUDITS_API_URL}/${auditId}/status`,
      data: {
        status,
      },
    },
  },
  meta,
});

export const patchStatusSuccess = (
  audit: DetailedAudit,
  meta: MetaAction = {},
): types.PatchAuditSuccessAction => ({
  type: actionTypes.PATCH_STATUS_SUCCESS,
  payload: audit,
  meta,
});

export const patchStatusFailure = (
  message: string,
  meta: MetaAction = {},
): types.PatchAuditFailureAction => ({
  type: actionTypes.PATCH_STATUS_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const startAudit = (auditId: string, meta: MetaAction = {}): types.StartAuditRequestAction => ({
  type: actionTypes.START_REQUEST,
  payload: {
    auditId,
    request: {
      method: 'POST',
      url: `${AUDITS_API_URL}/${auditId}/start`,
    },
  },
  meta,
});

export const startAuditSuccess = (audit: DetailedAudit, meta: MetaAction = {}): types.StartAuditSuccessAction => ({
  type: actionTypes.START_SUCCESS,
  payload: audit,
  meta,
});

export const startAuditFailure = (message: string, meta: MetaAction = {}): types.StartAuditFailureAction => ({
  type: actionTypes.START_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const patchAssignees = (
  auditId: string,
  data: AuditPatchAssigneesRequest,
  meta: MetaAction = {},
): types.PatchAssigneesRequestAction => ({
  type: actionTypes.PATCH_ASSIGNEES_REQUEST,
  payload: {
    auditId,
    request: {
      method: 'PATCH',
      url: `${AUDITS_API_URL}/${auditId}/assignees`,
      data,
    },
  },
  meta,
});

export const patchAssigneesSuccess = (
  audit: DetailedAudit,
  meta: MetaAction = {},
): types.PatchAssigneesSuccessAction => ({
  type: actionTypes.PATCH_ASSIGNEES_SUCCESS,
  payload: audit,
  meta,
});

export const patchAssigneesFailure = (
  message: string,
  meta: MetaAction = {},
): types.PatchAssigneesFailureAction => ({
  type: actionTypes.PATCH_ASSIGNEES_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const patchStartDate = (
  auditId: string,
  data: AuditPatchStartDateRequest,
  meta: MetaAction = {},
): types.PatchStartDateRequestAction => ({
  type: actionTypes.PATCH_START_DATE_REQUEST,
  payload: {
    auditId,
    request: {
      method: 'PATCH',
      url: `${AUDITS_API_URL}/${auditId}/startDate`,
      data,
    },
  },
  meta,
});

export const patchStartDateSuccess = (
  audit: DetailedAudit,
  meta: MetaAction = {},
): types.PatchStartDateSuccessAction => ({
  type: actionTypes.PATCH_START_DATE_SUCCESS,
  payload: audit,
  meta,
});

export const patchStartDateFailure = (
  message: string,
  meta: MetaAction = {},
): types.PatchStartDateFailureAction => ({
  type: actionTypes.PATCH_START_DATE_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const enableEditionMode = (meta: MetaAction = {}): types.EnableEditionAction => ({
  type: actionTypes.ENABLE_EDITION,
  meta,
});

export const disabledEditionMode = (meta: MetaAction = {}): types.DisableEditionAction => ({
  type: actionTypes.DISABLE_EDITION,
  meta,
});

export const fetchUsersSelect = (
  selectId: string,
  params: Record<string, unknown>,
  meta: MetaAction = {},
): types.FetchUsersSelectAction => ({
  type: actionTypes.FETCH_USERS_SELECT_REQUEST,
  payload: {
    selectId,
    params,
  },
  meta,
});

export const fetchNextUsersSelect = (
  params: Record<string, unknown>,
  selectId: string,
  meta: MetaAction = {},
): types.FetchNextUsersSelectAction => ({
  type: actionTypes.FETCH_NEXT_USERS_SELECT_REQUEST,
  payload: {
    params,
    selectId,
  },
  meta,
});

export const fetchUsersSelectSuccess = (
  { selectId, userIds, links, page }: NonNullable<types.FetchUsersSelectSuccessAction['payload']>,
  meta: MetaAction = {},
): types.FetchUsersSelectSuccessAction => ({
  type: actionTypes.FETCH_USERS_SELECT_SUCCESS,
  payload: {
    selectId,
    userIds,
    links,
    page,
  },
  meta,
});

export const clearUsersSelect = (selectId: string, meta: MetaAction = {}): types.ClearUsersSelectAction => ({
  type: actionTypes.CLEAR_USERS_SELECT,
  payload: {
    selectId,
  },
  meta,
});

export const postMarker = (
  {
    auditId,
    sectionId,
    questionId,
    marker,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    marker: types.NewExplodedViewMarker;
  },
  meta: MetaAction = {},
): types.PostMarkerRequestAction => ({
  type: actionTypes.POST_MARKER_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${AUDITS_API_URL}/${auditId}/questions/${questionId}/markers`,
      data: marker,
    },
    auditId,
    sectionId,
    questionId,
  },
  meta,
});

export const postMarkerSuccess = (
  {
    auditId,
    sectionId,
    questionId,
    marker,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    marker: ExplodedViewMarker;
  },
  meta: MetaAction = {},
): types.PostMarkerSuccessAction => ({
  type: actionTypes.POST_MARKER_SUCCESS,
  payload: { auditId, sectionId, questionId, marker },
  meta,
});

export const postMarkerFailure = (message: string, meta: MetaAction = {}): types.PostMarkerFailureAction => ({
  type: actionTypes.POST_MARKER_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const patchMarker = (
  {
    auditId,
    sectionId,
    questionId,
    marker,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    marker: ExplodedViewMarker;
  },
  meta: MetaAction = {},
): types.PatchMarkerRequestAction => ({
  type: actionTypes.PATCH_MARKER_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${AUDITS_API_URL}/${auditId}/questions/${questionId}/markers/${marker.id}`,
      data: marker,
    },
    auditId,
    sectionId,
    questionId,
  },
  meta,
});

export const patchMarkerSuccess = (
  {
    auditId,
    sectionId,
    questionId,
    marker,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    marker: ExplodedViewMarker;
  },
  meta: MetaAction = {},
): types.PatchMarkerSuccessAction => ({
  type: actionTypes.PATCH_MARKER_SUCCESS,
  payload: { auditId, sectionId, questionId, marker },
  meta,
});

export const patchMarkerFailure = (
  message: string,
  meta: MetaAction = {},
): types.PatchMarkerFailureAction => ({
  type: actionTypes.PATCH_MARKER_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const deleteMarker = (
  {
    auditId,
    sectionId,
    questionId,
    markerId,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    markerId: string;
  },
  meta: MetaAction = {},
): types.DeleteMarkerRequestAction => ({
  type: actionTypes.DELETE_MARKER_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${AUDITS_API_URL}/${auditId}/questions/${questionId}/markers/${markerId}`,
    },
    auditId,
    sectionId,
    questionId,
    markerId,
  },
  meta,
});

export const deleteMarkerSuccess = (
  {
    auditId,
    sectionId,
    questionId,
    markerId,
  }: {
    auditId: string;
    sectionId: string;
    questionId: string;
    markerId: string;
  },
  meta: MetaAction = {},
): types.DeleteMarkerSuccessAction => ({
  type: actionTypes.DELETE_MARKER_SUCCESS,
  payload: { auditId, sectionId, questionId, markerId },
  meta,
});

export const deleteMarkerFailure = (
  message: string,
  meta: MetaAction = {},
): types.DeleteMarkerFailureAction => ({
  type: actionTypes.DELETE_MARKER_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const openShareModal = (auditId: string, meta: MetaAction = {}): types.OpenShareModalAction => ({
  type: actionTypes.OPEN_SHARE_MODAL,
  payload: auditId,
  meta: {
    source: DETAILS_SHARE_MODAL,
    ...meta,
  },
});

export const closeShareModal = (meta: MetaAction = {}): types.CloseShareModalAction => ({
  type: actionTypes.CLOSE_SHARE_MODAL,
  meta,
});

export const fetchFOUrl = (
  auditId: string,
  params: types.FetchFOUrlParams,
  meta: MetaAction = {},
): types.FetchFOUrlAction => ({
  type: actionTypes.FETCH_FO_URL,
  payload: {
    request: {
      method: 'GET',
      url: `${AUDITS_API_URL}/${auditId}/link`,
      params,
    },
  },
  meta,
});

export const fetchFOUrlSuccess = (url: string, meta: MetaAction = {}): types.FetchFOUrlSuccessAction => ({
  type: actionTypes.FETCH_FO_URL_SUCCESS,
  payload: url,
  meta,
});

export const fetchFOUrlFailure = (message: string, meta: MetaAction = {}): types.FetchFOUrlFailureAction => ({
  type: actionTypes.FETCH_FO_URL_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const auditSend = (
  auditId: string,
  data: AuditSendRequest,
  meta: MetaAction = {},
): types.AuditSendRequestAction => ({
  type: actionTypes.SHARE_AUDIT,
  payload: {
    request: {
      method: 'POST',
      url: `${AUDITS_API_URL}/${auditId}/send`,
      data,
    },
  },
  meta,
});

export const auditSendSuccess = (
  payload: undefined,
  meta: MetaAction = {},
): types.AuditSendSuccessAction => ({
  type: actionTypes.SHARE_AUDIT_SUCCESS,
  payload,
  meta,
});

export const auditSendFailure = (message: string, meta: MetaAction = {}): types.AuditSendFailureAction => ({
  type: actionTypes.SHARE_AUDIT_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const setSelectedMarkerId = (
  markerId: string,
  meta: MetaAction = {},
): types.SetSelectedMarkerIdAction => ({
  type: actionTypes.SET_SELECTED_MARKER_ID,
  payload: { markerId },
  meta,
});

export const printCustomerView = (
  { auditId, fileName }: { auditId: string; fileName: string },
  meta: MetaAction = {},
): types.PrintCustomerViewAction => ({
  type: actionTypes.PRINT_CUSTOMER_VIEW,
  payload: {
    auditId,
    fileName,
    request: {
      method: 'GET',
      url: `${AUDITS_API_URL}/${auditId}/pdf`,
    },
  },
  meta,
});

export const printCustomerViewSuccess = (
  { fileName, auditId, data }: { fileName: string; auditId: string; data: string | { path: string } },
  meta: MetaAction = {},
): types.PrintCustomerViewSuccessAction => ({
  type: actionTypes.PRINT_CUSTOMER_VIEW_SUCCESS,
  payload: {
    fileName,
    auditId,
    data,
  },
  meta,
});

export const printCustomerViewFailure = (
  error: string,
  meta: MetaAction = {},
): types.PrintCustomerViewFailureAction => ({
  type: actionTypes.PRINT_CUSTOMER_VIEW_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchLinkedAudits = (auditId: string, meta: MetaAction = {}): types.FetchLinkedAuditsAction => ({
  type: actionTypes.FETCH_LINKED_AUDITS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${AUDITS_API_URL}/${auditId}/links`,
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

export const openDrawerPreview = (auditId: string, meta: MetaAction = {}): types.OpenDrawerPreviewAction => ({
  type: actionTypes.DRAWER_PREVIEW_OPEN,
  payload: auditId,
  meta,
});

export const closeDrawerPreview = (meta: MetaAction = {}): types.CloseDrawerPreviewAction => ({
  type: actionTypes.DRAWER_PREVIEW_CLOSE,
  meta,
});
